// src/App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import APLUS_QUESTIONS from "./questions_aplus";
import NETPLUS_QUESTIONS from "./questions_netplus";
import SECPLUS_QUESTIONS from "./questions_secplus";

/**
 * Multi-exam CompTIA Practice – A+ / Net+ / Sec+
 * - Exams: A+ (Core 1/Core 2/Both), Net+, Sec+
 * - Per-exam question banks & session persistence
 * - Dark mode toggle retained; “glassy” UI retained
 * - No exam mixing in one session (by design)
 */

// ---- Types (JSDoc) ---------------------------------------------------------
/**
 * @typedef {Object} QA
 * @property {string} id
 * @property {"Core 1"|"Core 2"|string} domain   // For A+, "Core 1" or "Core 2"; others may set exam label or any string
 * @property {string} subdomain
 * @property {string} stem
 * @property {string[]} choices // length 2..6
 * @property {number} answer // index into choices
 * @property {string} explanation
 * @property {"easy"|"medium"|"hard"} difficulty
 */

// ---- Constants --------------------------------------------------------------
const EXAMS = /** @type {const} */ (["A+", "Net+", "Sec+"]);
const APLUS_TRACKS = /** @type {const} */ (["Both", "Core 1", "Core 2"]);

const DEFAULTS = {
  "A+": APLUS_QUESTIONS,
  "Net+": NETPLUS_QUESTIONS,
  "Sec+": SECPLUS_QUESTIONS,
};

/** Per-exam question-bank localStorage keys */
const BANK_KEY = {
  "A+": "comp_exam_bank_aplus_v1",
  "Net+": "comp_exam_bank_netplus_v1",
  "Sec+": "comp_exam_bank_secplus_v1",
};

/** Per-exam session localStorage keys */
const SAVE_KEY = {
  "A+": "comp_exam_session_aplus_v1",
  "Net+": "comp_exam_session_netplus_v1",
  "Sec+": "comp_exam_session_secplus_v1",
};

const THEME_KEY = "comp_exam_theme_v1"; // 'light' | 'dark'

// ---- Helpers ---------------------------------------------------------------
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function sample(arr, n) {
  return shuffle(arr).slice(0, Math.max(0, Math.min(n, arr.length)));
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function prefersDark() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function loadBankForExam(exam) {
  const key = BANK_KEY[exam];
  const fallback = DEFAULTS[exam] || [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

function saveBankForExam(exam, bank) {
  const key = BANK_KEY[exam];
  try {
    localStorage.setItem(key, JSON.stringify(bank));
  } catch {
    // ignore
  }
}

// ---- Root Component --------------------------------------------------------
export default function App() {
  // Exam + A+ track selection
  const [exam, setExam] = useState(/** @type {"A+"|"Net+"|"Sec+"} */ ("A+"));
  const [aplusTrack, setAplusTrack] = useState(/** @type {"Both"|"Core 1"|"Core 2"} */ ("Both"));

  // Load bank per exam
  const [bank, setBank] = useState(() => loadBankForExam("A+"));

  // View + session config
  const [view, setView] = useState(/** @type {"setup"|"quiz"|"review"|"results"} */ ("setup"));
  const [count, setCount] = useState(20);
  const [timed, setTimed] = useState(true);
  const [minutes, setMinutes] = useState(20);

  // Quiz state
  const [quiz, setQuiz] = useState(/** @type {QA[]} */([]));
  const [answers, setAnswers] = useState(/** @type {(number|null)[]} */ ([]));
  const [flags, setFlags] = useState(/** @type {boolean[]} */ ([]));
  const [givenUp, setGivenUp] = useState(/** @type {boolean[]} */ ([]));
  const [idx, setIdx] = useState(0);
  const [deadline, setDeadline] = useState(/** @type {number|null} */(null)); // epoch ms
  const [now, setNow] = useState(Date.now());

  // THEME --------------------------------------------------------------
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || (prefersDark() ? "dark" : "light");
    } catch { return "light"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);

  // Tick
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // When exam changes: load its bank; try to restore its session
  useEffect(() => {
    setBank(loadBankForExam(exam));
    // Reset UI to setup when switching exams to avoid weird cross-state
    setView("setup");
    setQuiz([]);
    setAnswers([]);
    setFlags([]);
    setIdx(0);
    setDeadline(null);

    // Restore saved session for this exam, if present
    try {
      const raw = localStorage.getItem(SAVE_KEY[exam]);
      if (raw) {
        const s = JSON.parse(raw);
        if (s && s.view) {
          setView(s.view);
          if (exam === "A+" && s.aplusTrack) setAplusTrack(s.aplusTrack);
          setCount(s.count ?? 20);
          setTimed(s.timed ?? true);
          setMinutes(s.minutes ?? 20);
          setQuiz(s.quiz || []);
          setAnswers(s.answers || []);
          setFlags(s.flags || []);
          setGivenUp(s.givenUp || []);
          setIdx(s.idx || 0);
          setDeadline(s.deadline || null);
        }
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam]);

  // Save session snapshot per exam
  useEffect(() => {
    const payload = { view, exam, aplusTrack, count, timed, minutes, quiz, answers, flags, givenUp, idx, deadline };
    try {
      localStorage.setItem(SAVE_KEY[exam], JSON.stringify(payload));
    } catch { /* ignore */ }
  }, [view, exam, aplusTrack, count, timed, minutes, quiz, answers, flags, givenUp, idx, deadline]);


  // Filtered bank (A+ can filter by Core 1/Core 2/Both)
  const filteredBank = useMemo(() => {
    if (exam !== "A+") return bank;
    if (aplusTrack === "Both") return bank;
    return bank.filter((q) => q.domain === aplusTrack);
  }, [bank, exam, aplusTrack]);

  const timeLeft = useMemo(() => {
    if (!timed || !deadline) return null;
    const ms = Math.max(0, deadline - now);
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return { m, s, ms };
  }, [timed, deadline, now]);

  useEffect(() => {
    if (timeLeft && timeLeft.ms === 0 && view === "quiz") {
      handleSubmit();
    }
  }, [timeLeft, view]); // eslint-disable-line react-hooks/exhaustive-deps

  function startQuiz() {
    const picked = sample(filteredBank, count);
    // Shuffle choices per-question but keep ID
    const randomized = picked.map((q) => ({ ...q, choices: shuffle(q.choices) }));
    setQuiz(randomized);
    setAnswers(new Array(randomized.length).fill(null));
    setFlags(new Array(randomized.length).fill(false));
    setGivenUp(new Array(randomized.length).fill(false));
    setIdx(0);
    setDeadline(timed ? Date.now() + minutes * 60000 : null);
    setView("quiz");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetAll() {
    setView("setup");
    setQuiz([]);
    setAnswers([]);
    setFlags([]);
    setGivenUp([]);
    setIdx(0);
    setDeadline(null);
    window.scrollTo({ top: 0 });
  }

  function handleGiveUp() {
    setGivenUp((g) => g.map((v, i) => (i === idx ? true : v)));
    // Lock this question: do not set an answer — it will be treated as incorrect
  }

  function handleMark() {
    setFlags((f) => f.map((v, i) => (i === idx ? !v : v)));
  }

  function handleAnswer(choiceIdx) {
    setAnswers((a) => a.map((v, i) => (i === idx ? choiceIdx : v)));
  }

  function goto(delta) {
    setIdx((i) => Math.min(Math.max(0, i + delta), quiz.length - 1));
  }

  function handleSubmit() {
    setView("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function unansweredCount() {
    return answers.filter((a) => a === null).length;
  }

  const score = useMemo(() => {
    if (quiz.length === 0) return 0;
    let correct = 0;
    for (let i = 0; i < quiz.length; i++) {
      const q = quiz[i];
      const chosen = answers[i];
      if (chosen != null) {
        const original = bank.find((b) => b.id === q.id);
        if (original) {
          const correctText = original.choices[original.answer];
          const chosenText = q.choices[chosen];
          if (correctText === chosenText) correct++;
        }
      }
    }
    return correct;
  }, [answers, quiz, bank]);

  const byDomain = useMemo(() => {
    // Only meaningful for A+ (Core breakdown). Others won't render breakdown.
    /** @type {Record<string, {total:number, correct:number}>} */
    const stats = {};
    if (exam !== "A+") return stats;
    for (let i = 0; i < quiz.length; i++) {
      const q = quiz[i];
      const d = q.domain;
      if (!stats[d]) stats[d] = { total: 0, correct: 0 };
      stats[d].total++;
      const chosen = answers[i];
      const orig = bank.find((b) => b.id === q.id);
      if (orig && chosen != null) {
        const correctText = orig.choices[orig.answer];
        const chosenText = q.choices[chosen];
        if (correctText === chosenText) stats[d].correct++;
      }
    }
    return stats;
  }, [answers, quiz, bank, exam]);

  // Keyboard shortcuts (quiz)
  useEffect(() => {
    function onKey(e) {
      if (e.key.toLowerCase() === "t") {
        setTheme((t) => (t === "dark" ? "light" : "dark"));
      }
      if (view !== "quiz") return;
      if (["1", "2", "3", "4", "5", "6"].includes(e.key)) {
        const pick = parseInt(e.key, 10) - 1;
        const len = quiz[idx]?.choices?.length ?? 0;
        if (pick >= 0 && pick < len) handleAnswer(pick);
      } else if (e.key.toLowerCase() === "n") {
        goto(1);
      } else if (e.key.toLowerCase() === "p") {
        goto(-1);
      } else if (e.key.toLowerCase() === "r") {
        handleMark();
      } else if (e.key.toLowerCase() === "s") {
        handleSubmit();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, idx, quiz]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Question Bank Panel (per-exam) -------------------------------------
  const [bankOpen, setBankOpen] = useState(false);
  const [bankText, setBankText] = useState("");
  const bankTextAreaRef = useRef(/** @type {HTMLTextAreaElement|null} */(null));

  function doExport() {
    const text = JSON.stringify(bank, null, 2);
    setBankText(text);
    setBankOpen(true);
    setTimeout(() => bankTextAreaRef.current?.focus(), 0);
  }

  function doImport() {
    try {
      const parsed = JSON.parse(bankText);
      if (!Array.isArray(parsed)) throw new Error("JSON must be an array of questions");
      setBank(parsed);
      saveBankForExam(exam, parsed);
      window.alert(`Question bank imported for ${exam} and saved to your browser.`);
    } catch (e) {
      window.alert("Import failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  function doSaveBank() {
    saveBankForExam(exam, bank);
    window.alert(`Saved ${exam} bank to your browser.`);
  }

  function doResetBank() {
    const ok = window.confirm(`Reset ${exam} question bank to built-in defaults?`);
    if (!ok) return;
    const d = DEFAULTS[exam] || [];
    setBank(d);
    saveBankForExam(exam, d);
  }

  // ---- UI ------------------------------------------------------------------
  const badgeText = exam; // "A+" | "Net+" | "Sec+"

  // Logo click handler: confirm abandoning current session and return to setup
  function handleLogoActivate() {
    if (view !== "setup") {
      const ok = window.confirm("Abandon the current session and return to the homepage? Your progress will be lost.");
      if (!ok) return;
    }
    // Reset UI to the setup/home state
    resetAll();
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 text-slate-900 dark:text-slate-100 selection:bg-indigo-200/60 selection:text-slate-900 select-none">
  {/* Make header non-sticky on small screens to avoid covering content while scrolling on mobile. */}
  <header className="md:sticky top-0 z-10 border-b border-slate-200/70 dark:border-slate-800/80 backdrop-blur">
        <div className="bg-white/60 dark:bg-slate-900/50">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/**
               * Badge: A+ fits current size; Net+/Sec+ need a slightly wider badge to avoid clipping.
               * Make it keyboard accessible and clickable with a confirm when abandoning a session.
               */}
              <div
                role="button"
                tabIndex={0}
                onClick={handleLogoActivate}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleLogoActivate(); } }}
                className={classNames(
                  "rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white grid place-content-center shadow-sm",
                  exam === "A+" ? "w-9 h-9 font-bold" : "w-11 h-9 font-semibold text-sm",
                  "cursor-pointer"
                )}
                title={"Return to homepage"}
                aria-label={"Return to homepage"}
              >
                {badgeText}
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight">CompTIA Practice</h1>
                <p className="text-xs text-slate-600 dark:text-slate-400">No ads. Study fast.</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Jacob Olguin (Not affiliated with CompTIA)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {view === "quiz" && timeLeft && (
                <div
                  className={classNames(
                    "px-3 py-1 rounded-full text-sm font-medium border",
                    timeLeft.m < 1
                      ? "bg-rose-100/60 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800"
                      : "bg-slate-100/60 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200 border-slate-200 dark:border-slate-700"
                  )}
                  title="Time remaining"
                >
                  ⏳ {String(timeLeft.m).padStart(2, "0")}:{String(timeLeft.s).padStart(2, "0")}
                </div>
              )}
              <ThemeToggle theme={theme} setTheme={setTheme} />
              <button
                onClick={() => setBankOpen((v) => !v)}
                className="px-3 py-1.5 rounded-xl text-sm bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition"
                title={`Manage ${exam} question bank`}
              >
                Question Bank
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Bank Drawer */}
      {bankOpen && (
        <div className="border-b bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-800 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 py-4 grid gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={doExport} className="btn">Export JSON</button>
              <button onClick={doImport} className="btn btn-indigo">Import JSON</button>
              <button onClick={doSaveBank} className="btn btn-emerald">Save</button>
              <button onClick={doResetBank} className="btn btn-rose">Reset Defaults</button>
              <span className="text-xs text-slate-600 dark:text-slate-400">Questions in {exam} bank: {bank.length}</span>
            </div>
            <textarea
              ref={bankTextAreaRef}
              value={bankText}
              onChange={(e) => setBankText(e.target.value)}
              placeholder={`Paste a JSON array of questions for ${exam}. Schema:
[{ id, domain: "Core 1"|"Core 2"|string, subdomain, stem, choices: string[], answer: number, explanation, difficulty }].`}
              className="w-full h-56 p-3 border rounded-xl font-mono text-sm bg-white/60 dark:bg-slate-900/60 backdrop-blur border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
            />
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">
        {view === "setup" && (
          <section className="card grid gap-5">
            <h2 className="text-xl font-semibold tracking-tight">Create a Practice Session</h2>

            {/* Exam selector */}
            <div className="grid gap-2">
              <label className="label">Exam</label>
              <div className="flex flex-wrap gap-2">
                {EXAMS.map((x) => (
                  <button
                    key={x}
                    onClick={() => setExam(x)}
                    className={classNames("chip", exam === x && "chip-active")}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </div>

            {/* A+ track selector */}
            {exam === "A+" && (
              <div className="grid gap-2">
                <label className="label">A+ Track</label>
                <div className="flex flex-wrap gap-2">
                  {APLUS_TRACKS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setAplusTrack(t)}
                      className={classNames("chip", aplusTrack === t && "chip-active")}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Count + Timing */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <label className="label">Question Count</label>
                <input
                  type="number"
                  min={5}
                  max={100}
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value || "0", 10))}
                  className="input w-32"
                />
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <label className="label">Timing</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={timed} onChange={(e) => setTimed(e.target.checked)} />
                    Timed
                  </label>
                  {timed && (
                    <>
                      <span className="text-sm text-slate-600 dark:text-slate-400">Minutes</span>
                      <input
                        type="number"
                        min={5}
                        max={240}
                        value={minutes}
                        onChange={(e) => setMinutes(parseInt(e.target.value || "0", 10))}
                        className="input w-24"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Keyboard: 1–6 answer • N/P next/prev • R flag • S submit • T theme
              </p>
              <button onClick={startQuiz} className="btn btn-indigo">Start</button>
            </div>
          </section>
        )}

        {view === "quiz" && quiz.length > 0 && (
          <section className="grid gap-4">
            <ProgressBar current={idx + 1} total={quiz.length} flags={flags} answers={answers} givenUp={givenUp} onJump={(i)=>setIdx(i)} />
            <QuestionCard
              q={quiz[idx]}
              index={idx}
              total={quiz.length}
              selected={answers[idx]}
              flagged={flags[idx]}
              givenUp={givenUp[idx]}
              bank={bank}
              onAnswer={handleAnswer}
              onFlag={handleMark}
              onGiveUp={handleGiveUp}
              onPrev={() => goto(-1)}
              onNext={() => goto(1)}
            />

            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Unanswered: {unansweredCount()} • Flagged: {flags.filter(Boolean).length}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setView("review")} className="btn">Review All</button>
                <button onClick={handleSubmit} className="btn btn-emerald">Submit</button>
              </div>
            </div>
          </section>
        )}

        {view === "review" && (
          <section className="grid gap-4">
            <ProgressBar current={idx + 1} total={quiz.length} flags={flags} answers={answers} givenUp={givenUp} onJump={(i)=>setIdx(i)} />
            <div className="card grid gap-4">
              <h2 className="text-lg font-semibold">Quick Review</h2>
              <div className="grid gap-3 max-h-[60vh] overflow-auto pr-1">
                {quiz.map((q, i) => (
                  <button
                    key={q.id}
                    onClick={()=>setIdx(i)}
                    className={classNames(
                      "text-left p-3 rounded-xl border hover:bg-slate-50/60 dark:hover:bg-slate-800/50 border-slate-200 dark:border-slate-700 transition",
                      i===idx && "ring-2 ring-indigo-500/60"
                    )}
                  >
                    <div className="text-xs text-slate-500 dark:text-slate-400">Q{i+1} • {q.domain} • {q.subdomain}</div>
                    <div className="font-medium">{q.stem}</div>
                    <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">
                      Answer: {answers[i] != null ? q.choices[answers[i]] : <em className="text-slate-500">(blank)</em>}
                    </div>
                    {flags[i] && <div className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Flagged</div>}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => setView("quiz")} className="btn">Back to Quiz</button>
                <button onClick={handleSubmit} className="btn btn-emerald">Submit</button>
              </div>
            </div>
          </section>
        )}

        {view === "results" && (
          <Results
            exam={exam}
            quiz={quiz}
            answers={answers}
            bank={bank}
            score={score}
            onRetakeSame={() => { setView("quiz"); setIdx(0); setDeadline(timed ? Date.now() + minutes*60000 : null); }}
            onNewTest={resetAll}
            byDomain={byDomain}
          />
        )}
      </main>

      <footer className="py-6 text-center text-xs text-slate-500 dark:text-slate-400">
        Built for study use. Not affiliated with CompTIA. Import your own objectives-aligned questions.
      </footer>
    </div>
  );
}

// ---- Small styled primitives (Tailwind utility bundles) --------------------
function ThemeToggle({ theme, setTheme }) {
  const dark = theme === "dark";
  return (
    <button
      onClick={() => setTheme(dark ? "light" : "dark")}
      className="px-3 py-1.5 rounded-xl text-sm border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-800 shadow-sm transition"
      title="Toggle theme (T)"
    >
      {dark ? "☀️ Light" : "🌙 Dark"}
    </button>
  );
}

function ProgressBar({ current, total, flags, answers, givenUp, onJump }) {
  const answeredCount = answers.filter(a=>a!=null).length;
  const pct = total ? Math.round((answeredCount/total)*100) : 0;
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600 dark:text-slate-400">Question {current} of {total}</div>
        <div className="text-sm text-slate-600 dark:text-slate-400">Answered: {answeredCount}/{total} ({pct}%)</div>
      </div>

      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: total }).map((_, i) => {
          const answered = answers[i] != null;
          const flagged = flags[i];
          const gaveUp = givenUp?.[i];
          return (
            <button
              key={i}
              onClick={() => onJump(i)}
              className={classNames(
                "h-3.5 w-3.5 rounded-full transition ring-offset-1 ring-offset-transparent",
                i === current - 1 ? "ring-2 ring-indigo-500" : "",
                // Priority: givenUp (red) > flagged (amber) > answered (green) > unanswered (slate)
                gaveUp
                  ? "bg-rose-500 dark:bg-rose-600"
                  : flagged
                  ? "bg-amber-400 dark:bg-amber-500"
                  : answered
                  ? "bg-emerald-500"
                  : "bg-slate-300 dark:bg-slate-700"
              )}
              title={`Jump to Q${i+1}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function QuestionCard({ q, index, total, selected, flagged, givenUp, bank, onAnswer, onFlag, onGiveUp, onPrev, onNext }) {
  return (
    <div className="card grid gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Q{index + 1} • {q.domain} • {q.subdomain} • {q.difficulty}</div>
          <h3 className="text-lg font-semibold leading-snug mt-1">{q.stem}</h3>
        </div>
        <button
          onClick={onFlag}
          className={classNames(
            "px-3 py-1.5 rounded-lg border text-sm transition",
            flagged
              ? "bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-200"
              : "hover:bg-slate-50/60 dark:hover:bg-slate-800/50 border-slate-200 dark:border-slate-700"
          )}
        >
          {flagged ? "★ Flagged" : "☆ Flag"}
        </button>
      </div>

      <div className="grid gap-2">
        {q.choices.map((c, i) => (
          <label
            key={i}
            className={classNames(
              "flex items-center gap-3 p-3 border rounded-xl cursor-pointer transform transition hover:-translate-y-1 hover:shadow-lg transition-colors select-none",
              "hover:border-indigo-300 dark:hover:border-indigo-600",
              "bg-white/70 dark:bg-slate-900/60 backdrop-blur",
              "border-slate-200 dark:border-slate-700",
              selected===i && "border-indigo-500 bg-indigo-50/70 dark:bg-indigo-500/10 shadow-sm",
              givenUp && "opacity-60 cursor-not-allowed",
              "focus-within:ring-2 focus-within:ring-indigo-300 dark:focus-within:ring-indigo-600"
            )}
            onMouseDown={(e) => e.preventDefault()} 
          >
            {/* visually-hidden native radio for accessibility */}
            <input
              type="radio"
              name={`q-${q.id}`}
              className="sr-only outline-none appearance-none caret-transparent"
              checked={selected === i}
              onChange={() => !givenUp && onAnswer(i)}
              disabled={givenUp}
            />

            {/* custom radio */}
            <span className={classNames(
              "flex-none w-5 h-5 rounded-full border flex items-center justify-center relative",
              selected===i ? "border-indigo-600" : "border-slate-300 dark:border-slate-600",
              "bg-white/80 dark:bg-slate-900/70 transition-colors"
            )} aria-hidden>
              <span className={classNames(
                "rounded-full bg-indigo-600 dark:bg-indigo-400 absolute transition-transform duration-150 ease-out transform",
                selected===i ? "w-2.5 h-2.5 scale-100" : "w-2.5 h-2.5 scale-0"
              )} />
            </span>

            <span className="select-none">
              <span className="text-slate-400 dark:text-slate-500 mr-2">[{i+1}]</span>{c}
            </span>
          </label>
        ))}
      </div>

      {/* Reveal correct answer and explanation when user gives up */}
      {givenUp && (
        (() => {
          const orig = bank.find((b) => b.id === q.id);
          const correctText = orig ? orig.choices[orig.answer] : "(unknown)";
          const explanation = orig ? orig.explanation : "";
          return (
            <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-800 text-sm">
              <div className="font-medium text-rose-700 dark:text-rose-200">Correct answer: {correctText}</div>
              <div className="mt-1 text-slate-700 dark:text-slate-300">{explanation}</div>
            </div>
          );
        })()
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={onPrev} disabled={index===0} className="btn disabled:opacity-50">Prev (P)</button>
          <button onClick={onNext} disabled={index===total-1} className="btn disabled:opacity-50">Next (N)</button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onGiveUp} disabled={givenUp} className="btn btn-rose" title="Give up on this question (will show correct answer)">Get Answer</button>
        </div>
      </div>
    </div>
  );
}

function Results({ exam, quiz, answers, bank, score, onRetakeSame, onNewTest, byDomain }) {
  const percent = quiz.length ? Math.round((score / quiz.length) * 100) : 0;

  function isCorrect(i) {
    const q = quiz[i];
    const chosen = answers[i];
    if (chosen == null) return false;
    const orig = bank.find((b) => b.id === q.id);
    if (!orig) return false;
    return q.choices[chosen] === orig.choices[orig.answer];
  }

  const showBreakdown = exam === "A+" && Object.keys(byDomain).length > 0;

  return (
    <section className="grid gap-5">
      <div className="card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Results</h2>
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              Score: <span className="font-medium">{score}</span> / {quiz.length} ({percent}%)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onRetakeSame} className="btn">Retake Same Set</button>
            <button onClick={onNewTest} className="btn btn-indigo">New Test</button>
          </div>
        </div>

        {showBreakdown && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {Object.entries(byDomain).map(([d, s]) => (
              <div key={d} className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/60 backdrop-blur">
                <div className="text-sm text-slate-600 dark:text-slate-300">{d}</div>
                <div className="text-lg font-semibold">{s.correct} / {s.total}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-3">Review & Explanations</h3>
        <div className="grid gap-4">
          {quiz.map((q, i) => {
            const orig = bank.find((b) => b.id === q.id);
            const chosen = answers[i];
            const correctText = orig ? orig.choices[orig.answer] : "";
            const yourText = chosen != null ? q.choices[chosen] : "(blank)";
            const ok = isCorrect(i);
            return (
              <div key={q.id} className="p-4 border rounded-xl border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/60 backdrop-blur">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Q{i+1} • {q.domain} • {q.subdomain}</div>
                    <div className="font-medium mt-0.5">{q.stem}</div>
                  </div>
                  <div className={classNames(
                    "px-2 py-0.5 rounded-full text-xs",
                    ok ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
                       : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200"
                  )}>
                    {ok ? "Correct" : "Incorrect"}
                  </div>
                </div>
                <div className="mt-2 grid gap-1 text-sm">
                  <div><span className="text-slate-500 dark:text-slate-400">Your answer:</span> {yourText}</div>
                  <div><span className="text-slate-500 dark:text-slate-400">Correct:</span> {correctText}</div>
                  <div className="text-slate-700 dark:text-slate-300 mt-1">{orig?.explanation}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
